import React from 'react';

import LineItems, { useHasLineItems } from '../components/LineItems';
import { useHasOrderNumber } from '../components/OrderNumber';
import { useFeatureEnables } from '../dataProviders';
import { isGiftWrapped } from '../components/GiftWrapPanel';

import Card from './Card';
import { makeBtStyles } from '../Theme';


const useStyles = makeBtStyles( theme => ({
  scrollableSection: ({ showGiftLink }:{showGiftLink: boolean}) => ({
    paddingRight: '10px',
    [theme.breakpoints.up('lg')]: {
      maxHeight: ( showGiftLink ? '270px' : '310px'),
    },
    [theme.breakpoints.up('md')]: {
      overflow: 'auto',
      width: '260px',
      maxHeight: ( showGiftLink ? '270px' : '310px'),
    },
    [theme.breakpoints.only('md')]: {
      height: '290px',
    },
  })
}));

const useLineItemStyles = makeBtStyles( theme => ({
  item: {
    minHeight: '63px',
    marginBottom: '21px',
    '&.item--no-image': {
      minHeight: '63px',
      '& $name, & $quantity, & $options': {
        marginLeft: '20px'
      }
    }
  },
  image: {
    float: 'left',
    marginRight: '16px',
    width: '50px',
    height: '50px',
    border: '1px solid #DFE3E8',
    display: 'flex',
    '& img': {
      alignSelf: 'center',
      margin: '0 auto'
    }
  },
  name: {
    marginLeft: '66px',
    paddingRight: '20px',
    fontSize: '14px',
    lineHeight: '21px',
    display: '-webkit-box',
    overflow: 'hidden',
    '-webkit-line-clamp': '1',
    '-webkit-box-orient': 'vertical',
  },
  description: {
    marginLeft: '66px',
    paddingRight: '20px',
    fontSize: '12px',
    lineHeight: '18px',
    fontWeight: '300',
    display: '-webkit-box',
    overflow: 'hidden',
    '-webkit-line-clamp': '1',
    '-webkit-box-orient': 'vertical',
  },
  options: {
    marginLeft: '66px',
    paddingRight: '20px',
    fontSize: '12px',
    fontWeight: '300',
    lineHeight: '21px',
    listStyleType: 'none',
    padding: '0',
    marginTop: '0',
    marginBottom: '0',
    display: '-webkit-box',
    overflow: 'hidden',
    '-webkit-line-clamp': '1',
    '-webkit-box-orient': 'vertical'
  },
  optionItem: {
    display: 'inline',
    '&:after': {
      content: '", "'
    },
    '&:last-child:after': {
      display: 'none'
    }
  },
  optionLabel: {
    fontWeight: 'bold',
    textTransform: 'capitalize',
    display: 'none'
  },
  quantity: {
    display: 'table',
    marginLeft: '66px',
    fontSize: '12px',
    fontWeight: '300',
    lineHeight: '20px'
  },
  unitPrice: {
    display: 'table',
    marginLeft: '66px',
    fontSize: '12px',
    fontWeight: '300',
    lineHeight: '21px'
  },
  subtotal: {
    display: 'none'
  },
  collapseToggle: {
    [theme.breakpoints.up('md')]:{
      position: 'relative',
      bottom: '-5px',
    },
    fontSize: '14px',
    textTransform: 'lowercase',
    width: '100%',
  }
}));

export function useIsLineItemsCardShown() {
  const hasLineItems = useHasLineItems();
  const hasOrderNumber = useHasOrderNumber();
  const { showShipmentDetails } = useFeatureEnables();

  return !( (!hasLineItems && !hasOrderNumber) || !showShipmentDetails || isGiftWrapped );
}

export default function LineItemsCard({ className }:{
  className: string
}) {
  const { showGiftLink } = useFeatureEnables();
  const classes = useStyles({ showGiftLink });
  const lineItemClasses = useLineItemStyles();

  return (
    <Card className={className}>
      <div className={classes.scrollableSection}>
        <LineItems truncateToCount={2} classes={lineItemClasses} formatQuantity={ q => <>Qty {q}</> } />
      </div>
    </Card>
  );
}